import { RefObject, useEffect, useState } from 'react';

const DEFAULT_MIN_HEIGHT_PX = 240;
const DEFAULT_BOTTOM_PADDING_PX = 16;

type Params = {
  enabled: boolean;
  tableRegionRef: RefObject<HTMLElement | null>;
  headerRef?: RefObject<HTMLElement | null>;
  footerRef?: RefObject<HTMLElement | null>;
  minHeightPx?: number;
  bottomPaddingPx?: number;
};

export const useDynamicTableHeight = ({
  enabled,
  tableRegionRef,
  headerRef,
  footerRef,
  minHeightPx = DEFAULT_MIN_HEIGHT_PX,
  bottomPaddingPx = DEFAULT_BOTTOM_PADDING_PX,
}: Params): number | null => {
  const [maxHeightPx, setMaxHeightPx] = useState<number | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!enabled) {
      setMaxHeightPx(null);
      return;
    }

    let rafId = 0;

    const computeMaxHeight = () => {
      cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => {
        const tableTop = tableRegionRef.current?.getBoundingClientRect().top;
        if (tableTop == null) return;

        const footerEl = footerRef?.current;
        const footerHeight = footerEl?.offsetHeight ?? 0;
        const footerMarginTop = footerEl
          ? Number.parseFloat(window.getComputedStyle(footerEl).marginTop || '0') || 0
          : 0;

        const next = Math.floor(
          window.innerHeight - tableTop - footerHeight - footerMarginTop - bottomPaddingPx,
        );
        setMaxHeightPx(Math.max(minHeightPx, next));
      });
    };

    computeMaxHeight();

    window.addEventListener('resize', computeMaxHeight);

    const ResizeObserverCtor = window.ResizeObserver;
    const observed = [headerRef?.current, footerRef?.current].filter(Boolean) as HTMLElement[];
    const ro =
      ResizeObserverCtor && observed.length ? new ResizeObserverCtor(computeMaxHeight) : null;
    observed.forEach((el) => ro?.observe(el));

    return () => {
      window.removeEventListener('resize', computeMaxHeight);
      if (ro) ro.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [enabled, tableRegionRef, headerRef, footerRef, minHeightPx, bottomPaddingPx]);

  return maxHeightPx;
};
